// The menu as the web's own frame draws it: the outside's data (menu.ts), made
// once per frame (`ShellMenuProvider` in the shell), read by the sidebar, its
// folded column, the phone's Dock and the Dock's menu alike.
//
// Promise:
// - Every one of them draws the same entries, in the data's order, with the
//   data's labels; switching the place changes the data once for all.
// - The web adds only what a native app draws its own way: an entry's drawn
//   badge (`useBadge`, read aloud as the part words it), the place's context
//   around it and the place's mark (`extras`, by entry id).
// - Icons are drawn by their names; an entry whose icon has no name keeps its
//   component.
import { useRouter } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
} from "react";
import { useMeApp } from "../../app/context";
import type { NavItem } from "../../app/screen-part";
import { useShownAccount } from "../../me/account";
import { Icon, type IconKind, iconNameOf, isIconKind } from "../../ui/icon";
import type { MenuItem } from "../../ui/menu";
import { useShellFrame } from "../frame";
import { useNavTargets } from "../nav-memory";
import type { DockPlace } from "../phone";
import { useShortcutSheet } from "../shortcut-sheet";
import type {
  ShellMenu,
  ShellMenuEntry,
  ShellMenuItem,
  ShellRail,
} from "./contract";
import { type BuiltMenu, buildShellMenu } from "./menu";

type IconComponent = ComponentType<{ className?: string }>;

/** What the web has for an entry besides its data. */
export interface WebEntryExtras {
  /** The icon component, kept only when it has no name. */
  icon?: IconComponent;
  /** The drawn badge (a dot read aloud with the part's words). */
  useBadge?(): ReactNode;
  /** Around anything that calls `useBadge` (the place's context). */
  Scope?: ComponentType<{ children: ReactNode }>;
  /** Drawn after the other rows under a line (`NavItem.apart`). */
  apart?: boolean;
}

export interface ShellMenuState {
  menu: ShellMenu;
  run: BuiltMenu["run"];
  /** By entry id. */
  extras: ReadonlyMap<string, WebEntryExtras>;
  /** The host's place as it gave it (its mark is drawn, not data). */
  place: DockPlace | null;
  /** Does what choosing the entry (or menu item) of this id does. */
  select(id: string): void;
}

const ShellMenuContext = createContext<ShellMenuState | null>(null);

const noPlace = (): DockPlace | null => null;
const noRail = (): ShellRail | null => null;

// The same array for the app's life (its nav does not change).
const remembering = new WeakMap<readonly NavItem[], string[]>();
const rememberingOf = (nav: readonly NavItem[]): string[] => {
  let tos = remembering.get(nav);
  if (!tos) {
    tos = nav.filter((n) => n.remembers).map((n) => n.to);
    remembering.set(nav, tos);
  }
  return tos;
};

/**
 * The account's menu: settings, shortcuts, then the host's (sign out, ...).
 * Laid out for a phone, no shortcuts (no keyboard to press them on).
 */
export function useAccountMenuItems(): MenuItem[] {
  const app = useMeApp();
  const router = useRouter();
  const sheet = useShortcutSheet();
  const { phone } = useShellFrame();
  return [
    {
      id: "settings",
      label: m.shell_settings(),
      icon: "settings",
      onSelect: () => void router.navigate({ to: "/me/settings" }),
    },
    ...(phone
      ? []
      : [
          {
            id: "shortcuts",
            label: m.shell_shortcuts(),
            icon: "keyboard" as const,
            onSelect: sheet.open,
          },
        ]),
    ...(app.extensions.accountMenu?.() ?? []),
  ];
}

function useShellMenuSource(): ShellMenuState {
  const app = useMeApp();
  const router = useRouter();
  const account = useShownAccount();
  const accountMenu = useAccountMenuItems();
  // The host's hook, the same one for the app's life.
  const usePlace = app.extensions.dock?.usePlace ?? noPlace;
  const place = usePlace();
  const useRail = app.extensions.useRail ?? noRail;
  const rail = useRail();
  const navTo = useNavTargets(rememberingOf(app.nav));
  const extras = new Map<string, WebEntryExtras>();
  const keep = (
    id: string,
    icon: IconComponent,
    useBadge: (() => ReactNode) | undefined,
    Scope?: WebEntryExtras["Scope"],
    apart?: boolean,
  ) => {
    extras.set(id, {
      ...(iconNameOf(icon) ? {} : { icon }),
      ...(useBadge ? { useBadge } : {}),
      ...(Scope ? { Scope } : {}),
      ...(apart ? { apart } : {}),
    });
  };
  for (const n of app.nav)
    keep(`me:${n.to}`, n.icon, n.useBadge, undefined, n.apart);
  for (const i of place?.items ?? [])
    keep(`place:${i.id}`, i.icon, i.useBadge, place?.Scope);
  const built = buildShellMenu({
    account: {
      name: account.name,
      // Whole: the outside draws it, away from this page's base.
      image: account.image
        ? new URL(account.image, window.location.href).href
        : undefined,
      menu: accountMenu,
    },
    me: app.nav.map((n) => ({
      id: n.to,
      label: n.label(),
      icon: n.icon,
      to: navTo(n.to),
    })),
    place: place && {
      id: place.id,
      name: place.name,
      items: place.items.map((i) => ({
        id: i.id,
        label: i.label(),
        icon: i.icon,
        to: i.to,
        paths: i.paths,
      })),
      lists: place.lists,
      menu: place.menu,
      sidebar: place.sidebar,
    },
    rail,
  });
  return {
    ...built,
    extras,
    place,
    select(id) {
      const to = built.run.get(id);
      if (typeof to === "string") void router.navigate({ to });
      else to?.();
    },
  };
}

/** Around a frame: makes its menu once for all who draw it. */
export function ShellMenuProvider({ children }: { children: ReactNode }) {
  const value = useShellMenuSource();
  return (
    <ShellMenuContext.Provider value={value}>
      {children}
    </ShellMenuContext.Provider>
  );
}

export function useShellMenu(): ShellMenuState {
  const menu = useContext(ShellMenuContext);
  if (!menu) throw new Error("useShellMenu outside a shell frame");
  return menu;
}

/** The menu's place entries, by group. */
export const menuItems = (
  menu: ShellMenu,
  group: "me" | "place" | "list",
): ShellMenuItem[] =>
  menu.entries.filter(
    (e): e is ShellMenuItem =>
      e.type === "item" && e.id.startsWith(`${group}:`),
  );

/** The place's lists: each heading with the links under it. */
export function menuLists(
  menu: ShellMenu,
): { heading: string; links: ShellMenuItem[] }[] {
  const lists: { heading: string; links: ShellMenuItem[] }[] = [];
  for (const e of menu.entries) {
    if (e.type === "heading") lists.push({ heading: e.label, links: [] });
    else if (e.type === "item" && e.id.startsWith("list:"))
      lists.at(-1)?.links.push(e);
  }
  return lists;
}

/** A menu's entries as the web's menu items (choosing one selects its id). */
export function menuItemsOf(
  entries: readonly ShellMenuEntry[],
  select: (id: string) => void,
): (MenuItem | "separator")[] {
  const out: (MenuItem | "separator")[] = [];
  for (const e of entries) {
    if (e.type === "separator") out.push("separator");
    else if (e.type === "item")
      out.push({
        id: e.id,
        label: e.label,
        onSelect: () => select(e.id),
        ...(isIconKind(e.icon) ? { icon: e.icon } : {}),
        ...(e.danger ? { danger: true } : {}),
        ...(e.disabled ? { disabled: true } : {}),
      });
  }
  return out;
}

// One component per icon name, so a row's icon is the same element across
// renders (a fresh component would draw it anew each time).
const byName = new Map<string, IconComponent>();

/** The icon to draw for an entry: by its name, else the web's component. */
export function entryIcon(
  item: ShellMenuItem,
  extras: WebEntryExtras | undefined,
): IconComponent | undefined {
  if (extras?.icon) return extras.icon;
  const name = item.icon;
  if (!name) return undefined;
  let icon = byName.get(name);
  if (!icon) {
    icon = function NamedIcon({ className }: { className?: string }) {
      return <Icon kind={name as IconKind} className={className} />;
    };
    byName.set(name, icon);
  }
  return icon;
}
